"use client";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Camera, Images } from "lucide-react";
import { CameraSource } from "@capacitor/camera";
import { useState } from "react";
import { captureIssuePhoto, isNativePlatform } from "@/lib/issue-photo";
import { Id } from "@convex/_generated/dataModel";
import { PhotoCaptureSheet } from "./photo-capture-sheet";

interface PhotoSelectionSheetProps {
  blockId: Id<"blocks">;
  trigger?: React.ReactNode;
  onPhotoAdded?: (photoPath: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function PhotoSelectionSheet({
  blockId,
  trigger,
  onPhotoAdded,
  open: controlledOpen,
  onOpenChange,
}: PhotoSelectionSheetProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const [isCapturing, setIsCapturing] = useState(false);
  const [capturedPath, setCapturedPath] = useState<string | null>(null);
  const [showCaptureSheet, setShowCaptureSheet] = useState(false);

  const handleCameraCapture = async () => {
    if (!isNativePlatform()) {
      console.warn("Camera only available on native platform");
      return;
    }

    setIsCapturing(true);
    try {
      const photoPath = await captureIssuePhoto();
      if (photoPath) {
        setCapturedPath(photoPath);
        setShowCaptureSheet(true);
        setOpen(false);
      }
    } catch (error) {
      console.error("Failed to capture photo:", error);
    } finally {
      setIsCapturing(false);
    }
  };

  const handlePhotoLibrarySelect = async () => {
    if (!isNativePlatform()) {
      console.warn("Photo library only available on native platform");
      return;
    }

    setIsCapturing(true);
    try {
      const photoPath = await captureIssuePhoto(CameraSource.Photos);
      if (photoPath) {
        setCapturedPath(photoPath);
        setShowCaptureSheet(true);
        setOpen(false);
      }
    } catch (error) {
      console.error("Failed to select photo from library:", error);
    } finally {
      setIsCapturing(false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
        <SheetContent side="bottom" className="h-auto">
          <SheetHeader>
            <SheetTitle>Add Photo</SheetTitle>
          </SheetHeader>
          <div className="space-y-3 mt-6">
            <Button
              variant="outline"
              className="w-full h-14 text-base"
              onClick={handleCameraCapture}
              disabled={isCapturing}
            >
              <Camera className="h-5 w-5 mr-3" />
              Take Photo
            </Button>
            <Button
              variant="outline"
              className="w-full h-14 text-base"
              onClick={handlePhotoLibrarySelect}
              disabled={isCapturing}
            >
              <Images className="h-5 w-5 mr-3" />
              Choose from Library
            </Button>
            <Button
              variant="ghost"
              className="w-full h-12"
              onClick={() => setOpen(false)}
              disabled={isCapturing}
            >
              Cancel
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {capturedPath && (
        <PhotoCaptureSheet
          blockId={blockId}
          photoPath={capturedPath}
          open={showCaptureSheet}
          onOpenChange={setShowCaptureSheet}
          onSaved={() => {
            onPhotoAdded?.(capturedPath);
            setCapturedPath(null);
          }}
          onCancelled={() => setCapturedPath(null)}
        />
      )}
    </>
  );
}
